import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { paginatedResponseFormat } from "../../services/constants";
import Loader from "../../components/Molecules/Loader/Loader";
import useFetchDomains from "../../hooks/useFetchDomains";
import { useAuth } from "../../contexts/AuthContext";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import {
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import useFetchAllDomainLinkage from "../../hooks/useFetchAllDomainLinkage";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { useToast } from "../../services/utils";
import {
  deleteDomainLinkage,
  reactivateDomainLinkage,
} from "../../services/domainLinkageService";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Domain Linkage List
const DOMAIN_LINKAGE_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    domain_linkage_ids: [],
    source_domain_id: [],
    target_domain_id: [],
    source_domain_key: "",
    target_domain_key: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["domain_linkage_ids", "source_domain_id", "target_domain_id"],
    STRING_FIELDS: ["source_domain_key", "target_domain_key", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const DomainLinkage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  // Permission checks for domain linkage actions
  const canViewDomainLinkage = hasPermission("domain_linkage", "view");
  const canAddDomainLinkage = hasPermission("domain_linkage", "add");
  const canEditDomainLinkage = hasPermission("domain_linkage", "edit");
  const canDeleteDomainLinkage = hasPermission("domain_linkage", "delete");

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLinkageLoading, setIsLinkageLoading] = useState<boolean>(false);
  const [isDomainLoading, setIsDomainLoading] = useState<boolean>(false);
  const [domainsLinkageData, setDomainLinkageData] = useState<any>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [linkageToDelete, setLinkageToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...DOMAIN_LINKAGE_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    DOMAIN_LINKAGE_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        // Handle array fields (comma-separated IDs)
        if (
          (
            DOMAIN_LINKAGE_FILTER_CONFIG.FIELD_TYPES
              .ID_FIELDS as readonly string[]
          ).includes(field)
        ) {
          initialState[field] = urlValue
            .split(",")
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
        } else {
          initialState[field] = urlValue;
        }
      }
    });

    return initialState;
  });

  // Pagination configuration
  const defaultPage =getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(parseInt(defaultPage, 10));
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // Hooks to fetch data
  const [fetchedDomainsLinkageData]: any = useFetchAllDomainLinkage({
    searchFilterData,
    setIsLoading: setIsLinkageLoading,
    page,
    pSize,
  });
  const [domainsData] = useFetchDomains({ setIsLoading: setIsDomainLoading });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    DOMAIN_LINKAGE_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined) {
        // Handle array fields (convert to comma-separated string)
        if (
          (
            DOMAIN_LINKAGE_FILTER_CONFIG.FIELD_TYPES
              .ID_FIELDS as readonly string[]
          ).includes(field)
        ) {
          if (Array.isArray(value) && value.length > 0) {
            // Extract IDs from objects or use plain numbers
            const ids = value
              .map((item: any) => (typeof item === "object" ? item.id : item))
              .filter((id) => id !== null && id !== undefined);
            if (ids.length > 0) {
              newParams.set(field, ids.join(","));
            }
          }
        } else if (value !== "") {
          // Don't add default values to URL to keep it clean
          if (field === "status_filter" && value === "Active") {
            return; // Skip default status filter
          }
          newParams.set(field, String(value));
        }
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    // Wait for domains data to load before processing URL params
    if (domainsData.length === 0) {
      return;
    }

    const urlFilterState: any = {
      ...DOMAIN_LINKAGE_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    DOMAIN_LINKAGE_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        // Handle array fields (comma-separated IDs)
        if (
          (
            DOMAIN_LINKAGE_FILTER_CONFIG.FIELD_TYPES
              .ID_FIELDS as readonly string[]
          ).includes(field)
        ) {
          const ids = urlValue
            .split(",")
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));

          // Convert IDs to domain objects for autocomplete components
          if (field === "source_domain_id" || field === "target_domain_id") {
            urlFilterState[field] = ids
              .map((id) => {
                const domain = domainsData.find((d: any) => d.id === id);
                return domain
                  ? { id: domain.id, domain_name: domain.domain_name }
                  : null;
              })
              .filter((d) => d !== null);
          } else {
            // For domain_linkage_ids, keep as plain IDs
            urlFilterState[field] = ids;
          }
        } else {
          urlFilterState[field] = urlValue;
        }
      }
    });

    // Only update state if URL differs from current state
    const hasChanges = DOMAIN_LINKAGE_FILTER_CONFIG.URL_FIELDS.some((field) => {
      const urlVal = urlFilterState[field];
      const currentVal = searchFilterData[field];
      // For arrays, compare by converting to strings
      if (Array.isArray(urlVal) && Array.isArray(currentVal)) {
        const urlIds = urlVal.map((item: any) =>
          typeof item === "object" ? item.id : item
        );
        const currentIds = currentVal.map((item: any) =>
          typeof item === "object" ? item.id : item
        );
        return urlIds.join(",") !== currentIds.join(",");
      }
      return urlVal !== currentVal;
    });

    if (hasChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams, domainsData]);

  // Memoize domain data for fast lookup
  const domainsMap =
    domainsData?.reduce((acc: any, domain: any) => {
      acc[domain.id] = {
        name: domain.domain_name,
        isTrue: !!domain.domain_name,
      };
      return acc;
    }, {}) || {};

  // Update domain linkage data when fetched data changes
  useEffect(() => {
    if (!fetchedDomainsLinkageData) {
      setDomainLinkageData([]); // Clear data if no fetched data
      return;
    }
    if (domainsData.length === 0) {
      return; // Wait for domains data to load
    }
    // Create a map to identify relationships that should be merged//
    const relationshipMap = new Map();
    // First pass: create keys for each source-target domain pair
    fetchedDomainsLinkageData.forEach((item: any) => {
      const key = `${item.source_domain_id}_${item.target_domain_id}`;
      if (!relationshipMap.has(key)) {
        relationshipMap.set(key, []);
      }
      relationshipMap.get(key).push(item);
    });

    // Second pass: process the groups
    const result: any = [];
    let resultId = 1;

    // Process each relationship group
    relationshipMap.forEach((items, key) => {
      // Extract source and target domain IDs
      const [source_domain_id, target_domain_id] = key.split("_").map(Number);
      if (
        !domainsMap[source_domain_id]?.isTrue || // Source domain is invalid
        !domainsMap[target_domain_id]?.isTrue // Target domain is invalid
      ) {
        return; // Skip this entry if either domain name is invalid
      }

      // Check if we should merge these items
      const shouldMerge = items.length > 1;

      if (shouldMerge) {
        // Merge multiple items for the same source-target pair
        const mergedEntry = items.reduce(
          (acc: any, item: any, idx: number) => {
            acc.id = resultId++;
            acc.source_domain_key[idx + 1] = item.source_domain_key;
            acc.target_domain_key[idx + 1] = item.target_domain_key;
            acc.action_ids[idx + 1] = item.id;
            // Set is_active to true only if ALL items are active
            acc.is_active = acc.is_active && item.is_active;
            return acc;
          },
          {
            source_domain_id,
            target_domain_id,
            source_domain_key: {},
            target_domain_key: {},
            action_ids: {},
            is_active: true, // Start with true, will be set to false if any item is inactive
          }
        );
        result.push(mergedEntry);
      } else {
        // Single item, keep it as is
        const item = items[0];
        result.push({
          id: resultId++,
          source_domain_id: item.source_domain_id,
          source_domain_key: { 1: item.source_domain_key },
          target_domain_id: item.target_domain_id,
          target_domain_key: { 1: item.target_domain_key },
          action_ids: { 1: item.id },
          is_active: item.is_active, // Preserve the is_active field from the original item
        });
      }
    });

    setDomainLinkageData(result);
  }, [fetchedDomainsLinkageData, domainsData]);

  // Handlers
  const handelClickEvent = () => {
    navigate("/domain-linkage/add");
  };

  // Handle delete domain linkage
  const handleDeleteDomainLinkage = (linkage: any) => {
    setLinkageToDelete(linkage);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!linkageToDelete) return;

    try {
      setDeleteLoading(true);
      // Get the actual domain linkage ID from action_ids
      // For merged entries, we need to delete all individual linkages
      const linkageIds = Object.values(linkageToDelete.action_ids);

      // Delete all linkages in the group
      const deletePromises = linkageIds.map((id: any) =>
        deleteDomainLinkage(id)
      );
      const results = await Promise.all(deletePromises);

      // Check if all deletions were successful
      const allSuccessful = results.every(
        (result: any) => result && result.status === 200
      );

      if (allSuccessful) {
        const sourceDomain =
          domainsMap[linkageToDelete.source_domain_id]?.name || "Unknown";
        const targetDomain =
          domainsMap[linkageToDelete.target_domain_id]?.name || "Unknown";
        showToast(
          `Domain linkage from "${sourceDomain}" to "${targetDomain}" deleted successfully`,
          "success"
        );
        setDeleteConfirmOpen(false);
        setLinkageToDelete(null);

        // Update the linkages list to remove the deleted linkage
        setDomainLinkageData((prev: any) =>
          prev.filter((item: any) => item.id !== linkageToDelete.id)
        );
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to delete domain linkage", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate domain linkage
  const handleReactivate = async (linkage: any) => {
    setIsLoading(true);
    try {
      // Get the actual domain linkage ID from action_ids
      // For merged entries, we need to reactivate all individual linkages
      const linkageIds = Object.values(linkage.action_ids);

      // Reactivate all linkages in the group
      const reactivatePromises = linkageIds.map((id: any) =>
        reactivateDomainLinkage(id)
      );
      await Promise.all(reactivatePromises);

      const sourceDomain =
        domainsMap[linkage.source_domain_id]?.name || "Unknown";
      const targetDomain =
        domainsMap[linkage.target_domain_id]?.name || "Unknown";
      showToast(
        `Domain linkage from "${sourceDomain}" to "${targetDomain}" reactivated successfully`,
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setDomainLinkageData((prev: any) =>
        prev.map((item: any) =>
          item.id === linkage.id ? { ...item, is_active: true } : item
        )
      );
    } catch (error) {
      showToast("Error reactivating domain linkage", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize column definitions for the data grid
  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        minWidth: 20,
        flex: 0,
        renderCell: () => <></>,
        cellClassName: "no-padding-cell",
        headerClassName: "no-padding-cell",
      },
      {
        field: "source_domain_id",
        headerName: "Source Domain",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          const sourceDomain = domainsMap[params?.row?.source_domain_id];
          return <>{sourceDomain?.isTrue ? sourceDomain.name : "N/A"}</>;
        },
      },
      {
        field: "source_domain_key",
        headerName: "Source Column Key",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          return (
            <div className="d-block">
              {Object.entries(params.row.source_domain_key).map(
                ([key, value]: any) => {
                  return (
                    <>
                      <div
                        className=" d-flex align-items-center min-height-32"
                        key={key}
                      >
                        {key}: {value}
                      </div>
                    </>
                  );
                }
              )}
            </div>
          );
        },
      },
      {
        field: "target_domain_id",
        headerName: "Target Domain",
        flex: 1,
        renderCell: (params: any) => {
          const targetDomain = domainsMap[params?.row?.target_domain_id];
          return <>{targetDomain?.isTrue ? targetDomain.name : "N/A"}</>;
        },
      },
      {
        field: "target_domain_key",
        headerName: "Target Column Key",
        flex: 1,
        renderCell: (params: any) => {
          return (
            <div className="d-block">
              {Object.entries(params.row.target_domain_key).map(
                ([key, value]: any) => {
                  return (
                    <>
                      <div
                        className=" d-flex align-items-center min-height-32"
                        key={key}
                      >
                        {key}: {value}
                      </div>
                    </>
                  );
                }
              )}
            </div>
          );
        },
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: any) => {
          return params.value ? (
            <Tooltip title="Active" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Inactive" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 130,
        renderCell: (params: any) => {
          const isActive = params.row.is_active === true;
          const handleDelete = () => {
            handleDeleteDomainLinkage(params.row);
          };

          return (
            <>
              {/* For active records, show all action buttons */}
              {isActive && (
                <>
                  <div className="d-block">
                    {params &&
                      Object.entries(params?.row?.action_ids).length > 0 &&
                      Object.entries(params?.row?.action_ids).map(
                        ([key, value]: any) => {
                          return (
                            <div key={key}>
                              {canViewDomainLinkage && (
                                <Tooltip
                                  title="View Domain Linkage"
                                  placement="top"
                                  arrow
                                >
                                  <IconButton
                                    className="datagrid-action-btn"
                                    color="inherit"
                                    onClick={() =>
                                      navigate(`/domain-linkage/view/${value}`)
                                    }
                                  >
                                    <IconEyeBase />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canEditDomainLinkage && (
                                <Tooltip
                                  title="Edit Domain Linkage"
                                  placement="top"
                                  arrow
                                >
                                  <IconButton
                                    className="datagrid-action-btn"
                                    color="inherit"
                                    onClick={() =>
                                      navigate(`/domain-linkage/edit/${value}`)
                                    }
                                  >
                                    <IconEditBase />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          );
                        }
                      )}
                  </div>
                  {canDeleteDomainLinkage && (
                    <DropdownMenu
                      dropdownMenuItems={[
                        <IconButton
                          key="delete-domain-linkage"
                          className="datagrid-action-btn"
                          color="inherit"
                          onClick={() => handleDelete()}
                        >
                          <IconDeleteBlueSvg />
                          Delete Domain Linkage
                        </IconButton>,
                      ]}
                    />
                  )}
                </>
              )}

              {/* For inactive records, show ONLY Reactivate button */}
              {!isActive && canEditDomainLinkage && (
                <Tooltip
                  title="Reactivate Domain Linkage"
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleReactivate(params.row)}
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    ],
    [
      domainsMap,
      navigate,
      canViewDomainLinkage,
      canEditDomainLinkage,
      canDeleteDomainLinkage,
    ]
  );

  return (
    <Box>
      {(isLoading || isLinkageLoading || isDomainLoading) && (
        <Loader isLoading={isLoading || isLinkageLoading || isDomainLoading} />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => {
          setDeleteConfirmOpen(false);
          setLinkageToDelete(null);
        }}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Domain Linkage"
        dailogDescription={
          linkageToDelete
            ? `Are you sure you want to delete the domain linkage from "${
                domainsMap[linkageToDelete.source_domain_id]?.name || "Unknown"
              }" to "${
                domainsMap[linkageToDelete.target_domain_id]?.name || "Unknown"
              }"?`
            : ""
        }
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={[
                "source_domain_id",
                "target_domain_id",
                "domain_linkage_ids",
                "source_domain_key",
                "target_domain_key",
                "status_filter",
              ]}
              primaryFields={["source_domain_id", "target_domain_id"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
                includeDomainId: true, // Show domain IDs in dropdowns
              }}
            />
          </Grid>
          {canAddDomainLinkage && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Add Domain
                  Linkage
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={domainsLinkageData || []}
          loading={isLoading}
          dataListTitle={"Domain Linkage"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={336}
          isPaginationRequired={false}
        />
      </FlexBetween>
    </Box>
  );
};

export default DomainLinkage;
